import { Component, Inject, OnInit, ViewChild } from "@angular/core";
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";
import { TdspCreativeService } from "src/app/core/services/tdsp/tdsp-creative.service";
import { BdspCreativeService } from "src/app/core/services/bdsp/bdsp-creative.service";
import { forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { DatePickerCustomHeaderComponent } from "../date-picker-custom-header/date-picker-custom-header.component";
import { CommonService } from "src/app/core/services/common.service";
import { SelectionModel } from "@angular/cdk/collections";
import { ActionPreviewComponent } from "../action-preview/action-preview.component";

@Component({
  selector: "app-creative-bulk-edit",
  templateUrl: "./creative-bulk-edit.component.html",
  styleUrls: ["./creative-bulk-edit.component.scss"],
})
export class CreativeBulkEditComponent implements OnInit {
  dataSource: MatTableDataSource<any>;
  columns: string[] = [];
  isValid: boolean = false;
  editForms: Array<UntypedFormGroup> = [];
  allFolders: Array<any> = [];
  isSaving: boolean = false;
  dsp: "tdsp" | "bdsp";
  showThirdPartyPixel: boolean = false;
  minDate = new Date();
  clientTimeZone;
  datePickerHeader = DatePickerCustomHeaderComponent;
  selection = new SelectionModel<any>(true, []);
  isTdspThirdPartyTagRequired = false;
  isBdspThirdPartyTagRequired = false;

  // TDSP-specific fields
  tdspColumns: string[] = ["checkbox", "id", "name", "landing_page_url", "third_party_tag", "js_tag"];

  // BDSP-specific fields
  bdspColumns: string[] = ["checkbox", "id", "name", "landing_page_url", "start_date", "end_date", "third_party_tag", "third_party_pixels"];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<CreativeBulkEditComponent>,
    private formBuilder: UntypedFormBuilder,
    private tdspCreativeService: TdspCreativeService,
    private bdspCreativeService: BdspCreativeService,
    private commonService: CommonService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.minDate.setHours(0, 0, 0, 0);
    this.clientTimeZone = this.data.clientTimeZone
    this.dsp = this.data.dsp || "tdsp";
    this.allFolders = this.data.allFolders || [];
    this.showThirdPartyPixel = this.data.showThirdPartyPixel || false;
    const selectedAdType = this.data.selectedItems?.[0]?.ad_type;

    this.isTdspThirdPartyTagRequired = this.dsp === "tdsp" && this.data.selectedItems?.some(item => !!item.third_party_tag);
    this.isBdspThirdPartyTagRequired = this.dsp === "bdsp" && this.data.selectedItems?.some(item => !!item.third_party_tag?.content_tag);
    // Set columns based on DSP type
    if (this.dsp === "tdsp") {
      this.columns = [...this.tdspColumns];
      if (!this.showThirdPartyPixel || selectedAdType === "video" || selectedAdType === "native_video") {
        this.columns = this.columns.filter((c) => c !== "js_tag");
      }
    } else {
      this.columns = [...this.bdspColumns];
      if (!this.showThirdPartyPixel || selectedAdType === "video") {
        this.columns = this.columns.filter((c) => c !== "third_party_pixels");
      }
      if (selectedAdType === 'html5') {
        this.columns = this.columns.filter(c => c !== "landing_page_url");
      }
    }   

    this.dataSource = new MatTableDataSource(this.data.selectedItems);

    for (const item of this.data.selectedItems) {
      if (this.dsp === "tdsp") {
        this.editForms.push(this.createTdspForm(item));
      } else {
        this.editForms.push(this.createBdspForm(item));
      }
    }
    this.validate();
  }

  createTdspForm(item: any): UntypedFormGroup {
    return this.formBuilder.group({
      name: [item.name || "", [Validators.required, Validators.maxLength(255)]],
      third_party_tag: [item.third_party_tag || "", item.third_party_tag ? [this.commonService.requiredTrimmed()] : []],
      description: [item.description || "", [Validators.maxLength(200)]],
      landing_page_url: [ item.landing_page_url || "", [Validators.required, this.commonService.secureUrl()]],
      js_tag: [item.javascript_tags?.[0] || ""],
      folder: [item.folder ?? 0],
    });
  }

  createBdspForm(item: any): UntypedFormGroup {
    // Parse dates for mat-datepicker - need to convert to Date objects
    const startDate = item.start_date ? new Date(item.start_date) : null;
    const endDate = item.end_date ? new Date(item.end_date) : null;

    return this.formBuilder.group({
      name: [item.name || "", [Validators.required, Validators.maxLength(255)]],
      landing_page_url: [ item.click_url || "", item.ad_type === "html5" ? [] : (item.third_party_tag?.content_tag && item.ad_type !== "native_video_content_vast_vpaid_wrapper") ? [] : [Validators.required, this.commonService.secureUrl()], ],
      start_date: [startDate],
      end_date: [endDate],
      third_party_tag: [ item.third_party_tag?.content_tag || "", item.third_party_tag?.content_tag ? [this.commonService.requiredTrimmed()] : [], ],
      third_party_pixels: [ item.third_party_tag?.scripts?.join("\n") || "", ],
    });
  }

  formatPixelsToString(thirdPartyTag: any): string {
    if (!thirdPartyTag) return "";
    const pixels = [];
    if (thirdPartyTag.pixels && Array.isArray(thirdPartyTag.pixels)) {
      pixels.push(...thirdPartyTag.pixels);
    }
    if (thirdPartyTag.scripts && Array.isArray(thirdPartyTag.scripts)) {
      pixels.push(...thirdPartyTag.scripts);
    }
    return pixels.join("\n");
  }

  convertApiDateToDatetimeLocal(dateString: string): string {
    // Convert "2026-07-15 00:00:00" format to "2026-07-15T00:00" format for datetime-local input
    if (!dateString) return "";
    // Replace space with T and remove seconds
    const parts = dateString.split(" ");
    if (parts.length === 2) {
      const timeParts = parts[1].split(":");
      return `${parts[0]}T${timeParts[0]}:${timeParts[1]}`;
    }
    return dateString;
  }

  convertDatetimeLocalToApiDate(datetimeLocal: string): string {
    // Convert "2026-07-15T00:00" format back to "2026-07-15 00:00:00" format
    if (!datetimeLocal) return "";
    const [datePart, timePart] = datetimeLocal.split("T");
    if (datePart && timePart) {
      return `${datePart} ${timePart}:00`;
    }
    return datetimeLocal;
  }

  formatDateToApiFormat(date: any): string {
    // Convert Date object to API format "YYYY-MM-DD HH:mm:ss"
    if (!date) return "";
    
    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }
    
    return this.convertDatetimeLocalToApiDate(date);
  }

  formatStringToPixels(pixelsString: string): any {
    if (!pixelsString) {
      return { pixels: [], scripts: [] };
    }
    // Split by newline and filter empty lines
    const lines = pixelsString.split("\n").filter((line) => line.trim());
    const pixels = [];
    const scripts = [];

    for (const line of lines) {
      if (line.includes("<img") || line.includes("src=")) {
        pixels.push(line);
      } else {
        scripts.push(line);
      }
    }

    return { pixels, scripts };
  }

  validate() {
    this.isValid = this.editForms.every((form) => {
      const startDate = form.get("start_date")?.value;
      const endDate = form.get("end_date")?.value;

      if (
        (startDate && !endDate) ||
        (!startDate && endDate) ||
        (startDate && endDate && endDate < startDate)
      ) {
        return false;
      }

      return Object.keys(form.controls)
        .filter(control => control !== "start_date" && control !== "end_date")
        .every(control => form.get(control)?.valid);
    });
  }

  save() {
    if (!this.isValid || this.isSaving) return;
    this.isSaving = true;

    const calls = this.data.selectedItems.map((item: any, i: number) => {
      const formVal = this.editForms[i].value;

      if (this.dsp === "tdsp") {
        return this.saveTdspCreative(item, formVal, i);
      } else {
        return this.saveBdspCreative(item, formVal, i);
      }
    });

    forkJoin(calls).subscribe(() => {
      this.isSaving = false;
      this.closeModal(true);
    });
  }

saveTdspCreative(item: any, formVal: any, index: number) {
  const creativeData = {
    ...item,
    name: formVal.name,
    description: formVal.description,
    landing_page_url: formVal.landing_page_url,

    folder: formVal.folder === 0 ? null : formVal.folder,
    third_party_tag: formVal.third_party_tag?.trim() || null,
  };

  if (item.ad_type != "native_video" && creativeData.asset_details != 0) {
    delete creativeData.asset_details
  }

  // JS tag — only include if third party pixel is allowed
  if (this.showThirdPartyPixel) {
    if (formVal.js_tag) {
      const existingTags = Array.isArray(item.javascript_tags)
        ? [...item.javascript_tags]
        : [];

      existingTags[0] = formVal.js_tag;
      creativeData.javascript_tags = existingTags;
    } else {
      creativeData.javascript_tags = item.javascript_tags || [];
    }
  } else {
    creativeData.javascript_tags = item.javascript_tags || [];
  }

  const formData = new FormData();
  formData.append("creative", JSON.stringify(creativeData));

  return this.tdspCreativeService.update(
    item.id,
    formData,
    this.data.globalParams
  ).pipe(
    catchError(() => of(null))
  );
}

  saveBdspCreative(item: any, formVal: any, index: number) {
    const creativeData: any = {
      id: item.id,
      name: formVal.name,
      click_url: (item.third_party_tag?.content_tag && item.ad_type !== 'native_video_content_vast_vpaid_wrapper') ? null : formVal.landing_page_url,
      start_date: formVal.start_date ? this.formatDateToApiFormat(formVal.start_date) : null,
      end_date: formVal.end_date ? this.formatDateToApiFormat(formVal.end_date) : null,
      ad_type: item.ad_type,
      state: item.state,
      width: item.width,
      height: item.height,
      third_party_tag: {
        pixels: item.third_party_tag?.pixels || [],
        scripts: formVal.third_party_pixels ? formVal.third_party_pixels .split("\n") .map((s: string) => s.trim()) .filter(Boolean) : [],
        content_tag: formVal.third_party_tag,
      },
      is_active: item.is_active || true,
      secure: item.secure,
      native_offer: item.native_offer?.id || null,
      apnx_audit_status: item.apnx_audit_status,
      adx_audit_status: item.adx_audit_status,
      confiant_audit_status: item.confiant_audit_status,
      attributes: item.attributes,
      creative_addons: item.creative_addons,
      bdsp_id: item.bdsp_id,
      description: item.description,
      details: item.details,
      apnx_audit_feedback: item.apnx_audit_feedback,
      adx_audit_feedback: item.adx_audit_feedback,
      confiant_audit_feedback: item.confiant_audit_feedback,
      advertiser: item.advertiser,
      agency_advertiser: item.agency_advertiser,
      is_asset_changed: item.is_asset_changed || false,
    };

    if(item.ad_type == "html5") {
      delete creativeData.click_url;
    }

    const formData = new FormData();
    formData.append("creative", JSON.stringify(creativeData));



    return this.bdspCreativeService.update(item.id, formData, this.data.globalParams).pipe(
      catchError(() => of(null))
    );
  }

  closeModal(save: boolean = false) {
    if (save) {
      this.dialogRef.close({
        save: true,
      });
      return;
    }
    let dialogReference = this.dialog.open(ActionPreviewComponent, {
      width: "25%",
      panelClass: "actionpreview-modalbox",
      disableClose: true,
      autoFocus: false,
      data: {
        action:
          "Unsaved changes will be lost. Please click OK to proceed without saving or CANCEL to continue editing.",
      },
    });
    dialogReference.afterClosed().subscribe((result) => {
      if (result.save) {
        this.dialogRef.close({
          save: save,
        });
      }
    });
  }

  copyLandingPageUrl(index: number, copyToAll: boolean): void {
    const value = this.editForms[index].get("landing_page_url")?.value;

    if (copyToAll) {
      for (let i = index + 1; i < this.editForms.length; i++) {
        this.editForms[i].get("landing_page_url")?.setValue(value);
      }
    } else if (index + 1 < this.editForms.length) {
      this.editForms[index + 1]
        .get("landing_page_url")
        ?.setValue(value);
    }

    this.validate();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.data.forEach((row) => this.selection.select(row));
  }

  isSomeSelected() {
    return this.selection.selected.length > 0;
  }

  deleteItems() {
    this.selection.selected.forEach((selected) => {
      const index = this.data.selectedItems.indexOf(selected);

      this.editForms.splice(index, 1);
      this.data.selectedItems.splice(index, 1);
    });

    this.selection.clear();

    this.data.selectedItems = [...this.data.selectedItems];
    this.dataSource = new MatTableDataSource(this.data.selectedItems);

    this.validate();
  }
}
